import type { Landmark, Viewpoint } from "./types";

export interface ViewCalibration {
  centerX: number;
  centerY: number;
  centerZ: number;
  orientation: "portrait" | "landscape";
}

export interface EyePosition {
  x: number;
  y: number;
  z: number;
}

export interface OffAxisFrustum {
  left: number;
  right: number;
  top: number;
  bottom: number;
  near: number;
  far: number;
}

export type CalibrationProgress = "none" | "progress" | "complete" | "failed";

export const NOMINAL_EYE_Z = 1.5;
const VALID_SMOOTHING_MS = 70;
const RECENTER_SMOOTHING_MS = 115;
const TRACKING_HOLD_MS = 100;

function visibility(point: Landmark | undefined): number {
  return point?.visibility ?? 0;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function currentOrientation(width = window.innerWidth, height = window.innerHeight): "portrait" | "landscape" {
  return height >= width ? "portrait" : "landscape";
}

export function extractViewpoint(landmarks: Landmark[] | null): Viewpoint | null {
  if (!landmarks) return null;
  const leftEye = landmarks[2];
  const rightEye = landmarks[5];
  const nose = landmarks[0];
  if (!leftEye || !rightEye || !nose) return null;

  const confidence = Math.min(visibility(leftEye), visibility(rightEye), visibility(nose));
  const eyeSpan = Math.hypot(leftEye.x - rightEye.x, leftEye.y - rightEye.y);
  if (confidence < 0.4 || eyeSpan < 0.015 || eyeSpan > 0.35) return null;

  return {
    x: (leftEye.x + rightEye.x) / 2,
    y: (leftEye.y + rightEye.y) / 2,
    z: 1 / eyeSpan,
    confidence,
  };
}

export function createCalibration(
  samples: Viewpoint[],
  orientation: ViewCalibration["orientation"],
): ViewCalibration | null {
  const valid = samples.filter((sample) => sample.confidence >= 0.4 && Number.isFinite(sample.z));
  if (valid.length < 5) return null;
  return {
    centerX: median(valid.map((sample) => sample.x)),
    centerY: median(valid.map((sample) => sample.y)),
    centerZ: median(valid.map((sample) => sample.z)),
    orientation,
  };
}

export function mapViewpoint(
  viewpoint: Viewpoint,
  calibration: ViewCalibration,
  viewportAspect: number,
): EyePosition {
  const screenWidth = 1;
  const screenHeight = 1 / Math.max(0.2, viewportAspect);
  const x = -(viewpoint.x - calibration.centerX) * screenWidth * 2;
  const y = (calibration.centerY - viewpoint.y) * screenHeight * 1.5;
  const z = NOMINAL_EYE_Z * (viewpoint.z / calibration.centerZ);
  return {
    x: clamp(x, -screenWidth * 0.35, screenWidth * 0.35),
    y: clamp(y, -screenHeight * 0.25, screenHeight * 0.25),
    z: clamp(z, NOMINAL_EYE_Z * 0.7, NOMINAL_EYE_Z * 1.6),
  };
}

export function computeOffAxisFrustum(
  eye: EyePosition,
  screenWidth: number,
  screenHeight: number,
  near = 0.05,
  far = 20,
): OffAxisFrustum {
  const scale = near / Math.max(0.1, eye.z);
  return {
    left: (-screenWidth / 2 - eye.x) * scale,
    right: (screenWidth / 2 - eye.x) * scale,
    top: (screenHeight / 2 - eye.y) * scale,
    bottom: (-screenHeight / 2 - eye.y) * scale,
    near,
    far,
  };
}

export class ViewpointController {
  private calibration: ViewCalibration | null = null;
  private samples: Viewpoint[] = [];
  private calibrationStartedAt = -Infinity;
  private calibrationOrientation: ViewCalibration["orientation"] = "portrait";
  private current: EyePosition = { x: 0, y: 0, z: NOMINAL_EYE_Z };
  private target: EyePosition = { ...this.current };
  private lastObservationAt = -Infinity;
  private lastObservation: Viewpoint | null = null;

  constructor(calibration?: ViewCalibration | null) {
    this.calibration = calibration ?? null;
  }

  get isCalibrated(): boolean {
    return Boolean(this.calibration);
  }

  get isCalibrating(): boolean {
    return Number.isFinite(this.calibrationStartedAt);
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  get savedCalibration(): ViewCalibration | null {
    return this.calibration;
  }

  beginCalibration(now: number, orientation = currentOrientation()): void {
    this.calibration = null;
    this.samples = [];
    this.calibrationStartedAt = now;
    this.calibrationOrientation = orientation;
    this.lastObservation = null;
    this.target = { x: 0, y: 0, z: NOMINAL_EYE_Z };
  }

  reset(): void {
    this.calibration = null;
    this.samples = [];
    this.calibrationStartedAt = -Infinity;
    this.lastObservation = null;
    this.current = { x: 0, y: 0, z: NOMINAL_EYE_Z };
    this.target = { ...this.current };
  }

  ingest(viewpoint: Viewpoint | null, now: number, viewportAspect: number): CalibrationProgress {
    if (viewpoint) {
      this.lastObservation = viewpoint;
      this.lastObservationAt = now;
      if (this.calibration) this.updateTarget(viewpoint, viewportAspect);
    }

    if (!this.isCalibrating) return "none";
    if (viewpoint) this.samples.push(viewpoint);
    const elapsed = now - this.calibrationStartedAt;
    if (this.samples.length >= 10 || elapsed >= 650) {
      const calibration = createCalibration(this.samples, this.calibrationOrientation);
      this.calibrationStartedAt = -Infinity;
      this.samples = [];
      if (!calibration) return "failed";
      this.calibration = calibration;
      this.lastObservation = viewpoint;
      if (viewpoint) this.target = mapViewpoint(viewpoint, calibration, viewportAspect);
      return "complete";
    }
    return "progress";
  }

  update(now: number, deltaMs: number, viewportAspect: number): EyePosition {
    const trackingFresh = now - this.lastObservationAt <= TRACKING_HOLD_MS;
    if (!trackingFresh) {
      this.target = { x: 0, y: 0, z: NOMINAL_EYE_Z };
    }
    const tau = trackingFresh ? VALID_SMOOTHING_MS : RECENTER_SMOOTHING_MS;
    const alpha = 1 - Math.exp(-Math.max(0, deltaMs) / tau);
    this.current.x += (this.target.x - this.current.x) * alpha;
    this.current.y += (this.target.y - this.current.y) * alpha;
    this.current.z += (this.target.z - this.current.z) * alpha;
    return { ...this.current };
  }

  private updateTarget(viewpoint: Viewpoint, viewportAspect: number): void {
    if (!this.calibration) return;
    const mapped = mapViewpoint(viewpoint, this.calibration, viewportAspect);
    // Reject sub-pixel face-landmark noise while retaining visible head movement.
    if (Math.abs(mapped.x - this.target.x) >= 0.008) this.target.x = mapped.x;
    if (Math.abs(mapped.y - this.target.y) >= 0.012) this.target.y = mapped.y;
    if (Math.abs(mapped.z - this.target.z) >= 0.025) this.target.z = mapped.z;
  }
}
